import React, { useCallback, useEffect, useState } from 'react';
import { AuthenticationService, Tracking, UserManagementService } from 'api';
import { deleteCookie, getCookie } from 'utils';
import { User } from 'types';
import { extractUserData } from 'utils';
import * as Sentry from '@sentry/react';
import { AxiosUtils } from '../api/AxiosClient/AxiosClient';
import { AxiosError } from 'axios';

export enum APP_STATE {
  LOADING = 'loading',
  READY = 'ready',
}

interface ContextUser extends User {
  accessToken?: string;
  refreshToken?: string;
}

type AppContextType = {
  appState: string;
  user?: ContextUser;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

export const AppContext = React.createContext<AppContextType>({
  appState: APP_STATE.LOADING,
  user: undefined,
  login: () => Promise.resolve(undefined),
  register: () => Promise.resolve(undefined),
  logout: () => undefined,
});

export const AppProvider: React.FC<{ initialState?: AppContextType }> = ({ children, initialState }) => {
  const [state, setState] = useState<AppContextType>(
    initialState || {
      appState: APP_STATE.LOADING,
      user: undefined,
      login: () => Promise.resolve(undefined),
      register: () => Promise.resolve(undefined),
      logout: () => undefined,
    },
  );

  const logout = useCallback(() => {
    deleteCookie('access-token');
    deleteCookie('refresh-token');
    Tracking.unsetUser();
    setState(oldState => ({ ...oldState, user: undefined }));
  }, []);

  const login = useCallback((email: string, password: string) => {
    return AuthenticationService.authenticate(email, password).then(({ token, refreshToken }) => {
      const userData = extractUserData(token);
      if (!userData) {
        throw new Error('Jeton de connexion invalide');
      } else {
        const user = { ...userData, accessToken: token, refreshToken };
        Tracking.setUser(user);
        Sentry.configureScope(scope => {
          scope.setTag('isPro', userData.isPro);
        });
        setState(oldState => ({ ...oldState, user }));
      }
    });
  }, []);

  const register = useCallback((email, password) => {
    return UserManagementService.createUser(email, password).then(({ token, refreshToken }) => {
      const userData = extractUserData(token);
      if (!userData) {
        throw new Error('Jeton de connexion invalide');
      } else {
        const user = { ...userData, accessToken: token, refreshToken };
        Tracking.setUser(user);
        Sentry.configureScope(scope => {
          scope.setTag('isPro', userData.isPro);
        });
        setState(oldState => ({ ...oldState, user }));
      }
    });
  }, []);

  useEffect(() => {
    if (state.appState !== APP_STATE.READY) {
      const refreshToken = getCookie('refresh-token');
      let user: ContextUser | undefined;

      new Promise<void>(async resolve => {
        if (refreshToken) {
          try {
            const data = await AuthenticationService.authenticateWithRefreshToken(refreshToken);
            const userData = extractUserData(data.token);
            if (userData) {
              user = {
                ...userData,
                accessToken: data.token,
                refreshToken: data.refreshToken,
              };
              Sentry.configureScope(scope => {
                scope.setTag('isPro', userData.isPro);
              });
              Tracking.setUser(user);
            } else {
              deleteCookie('access-token');
              deleteCookie('refresh-token');
            }
          } catch (e) {
            Sentry.captureException(e, {
              tags: {
                api: 'UM',
                endpoint: 'authenticate',
                ...AxiosUtils.enrichTags((e as AxiosError).response),
              },
            });
            deleteCookie('access-token');
            deleteCookie('refresh-token');
          }
        }
        resolve();
      }).then(() => {
        setState({
          user,
          appState: APP_STATE.READY,
          logout,
          login,
          register,
        });
      });
    } else {
      setState(oldState => ({ ...oldState, login, logout, register }));
    }
  }, [state.appState, logout, login, register]);

  return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => React.useContext(AppContext);
